import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Brain,
  Info,
  Trophy,
  Clock,
  HelpCircle,
  BarChart2,
  XCircle,
  AlertTriangle,
  Check,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

const SudokuChallenge = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // ----------------------------------
  //           GAME STATES
  // ----------------------------------
  const [gameMode, setGameMode] = useState("intro"); // "intro" | "tutorial" | "play" | "results"
  const [difficulty, setDifficulty] = useState(1);

  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);

  const [timeElapsed, setTimeElapsed] = useState(0);
  const timerRef = useRef(null);

  // Game Board
  const [sudokuBoard, setSudokuBoard] = useState([]);
  const [originalBoard, setOriginalBoard] = useState([]);
  const [solvedBoard, setSolvedBoard] = useState([]);
  const [notes, setNotes] = useState(
    Array(9)
      .fill()
      .map(() =>
        Array(9)
          .fill()
          .map(() => Array(9).fill(false))
      )
  );
  const [isNoteMode, setIsNoteMode] = useState(false);
  const [errors, setErrors] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [gameWasCompleted, setGameWasCompleted] = useState(false);

  // Scoring & Performance
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);

  // UI / tutorial
  const [showInstructions, setShowInstructions] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [message, setMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);

  // ----------------------------------
  //         FETCH HIGH SCORES
  // ----------------------------------
  useEffect(() => {
    if (!organizationId) return;
    const fetchScores = async () => {
      try {
        const docRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "sudoku-challenge"
        );
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const scoresForDifficulty =
            data.leaderboards && data.leaderboards[difficulty.toString()]
              ? data.leaderboards[difficulty.toString()]
              : [];
          setHighScores(scoresForDifficulty);
        } else {
          setHighScores([]);
        }
      } catch (err) {
        console.error("Error fetching high scores:", err);
      }
    };
    fetchScores();
  }, [organizationId, difficulty]);

  // ----------------------------------
  //        TIMER LOGIC
  // ----------------------------------
  useEffect(() => {
    if (isPlaying && !isPaused) {
      timerRef.current = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, isPaused]);

  // ----------------------------------
  //       SUDOKU GENERATOR LOGIC
  // ----------------------------------
  // Helper function to check if a number is valid in a given position
  const isValid = (board, row, col, num) => {
    // Check row
    for (let x = 0; x < 9; x++) {
      if (board[row][x] === num) {
        return false;
      }
    }

    // Check column
    for (let y = 0; y < 9; y++) {
      if (board[y][col] === num) {
        return false;
      }
    }

    // Check 3x3 box
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let y = boxRow; y < boxRow + 3; y++) {
      for (let x = boxCol; x < boxCol + 3; x++) {
        if (board[y][x] === num) {
          return false;
        }
      }
    }

    return true;
  };

  // Helper function to solve a Sudoku board
  const solveSudoku = (board) => {
    const emptyCell = findEmptyCell(board);
    if (!emptyCell) {
      return true;
    }

    const [row, col] = emptyCell;

    for (let num = 1; num <= 9; num++) {
      if (isValid(board, row, col, num)) {
        board[row][col] = num;

        if (solveSudoku(board)) {
          return true;
        }

        board[row][col] = 0;
      }
    }

    return false;
  };

  // Helper function to find an empty cell in the board
  const findEmptyCell = (board) => {
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (board[row][col] === 0) {
          return [row, col];
        }
      }
    }
    return null;
  };

  // Function to generate a complete solved Sudoku board
  const generateSolvedBoard = () => {
    const board = Array(9)
      .fill()
      .map(() => Array(9).fill(0));

    // Fill the board with a valid solution
    solveSudoku(board);

    return board;
  };

  // Function to create a puzzle by removing numbers from a solved board
  const createPuzzle = (solvedBoard, difficulty) => {
    // Deep copy the solved board
    const puzzle = JSON.parse(JSON.stringify(solvedBoard));

    // Determine how many cells to remove based on difficulty
    let cellsToRemove;
    if (difficulty === 1) cellsToRemove = 40; // Easy
    else if (difficulty === 2) cellsToRemove = 50; // Medium
    else cellsToRemove = 60; // Hard

    // Remove cells randomly
    let removed = 0;
    while (removed < cellsToRemove) {
      const row = Math.floor(Math.random() * 9);
      const col = Math.floor(Math.random() * 9);

      if (puzzle[row][col] !== 0) {
        puzzle[row][col] = 0;
        removed++;
      }
    }

    return puzzle;
  };

  // ----------------------------------
  //        GAME LOGIC
  // ----------------------------------
  // Start the game
  // Start game function remains the same
  const startGame = () => {
    setScore(0);
    setErrors(0);
    setHintsUsed(0);
    setTimeElapsed(0);
    setIsComplete(false);

    // Generate a new Sudoku puzzle
    const solved = generateSolvedBoard();
    setSolvedBoard(solved);

    const puzzle = createPuzzle(solved, difficulty);
    setSudokuBoard(JSON.parse(JSON.stringify(puzzle)));
    setOriginalBoard(JSON.parse(JSON.stringify(puzzle)));

    // Reset notes
    setNotes(
      Array(9)
        .fill()
        .map(() =>
          Array(9)
            .fill()
            .map(() => Array(9).fill(false))
        )
    );

    setIsPlaying(true);
    setIsPaused(false);
    setMessage("");
    setShowMessage(false);
  };

  // Updated end game function with new scoring logic
  const endGame = () => {
    setIsPlaying(false);
    setGameMode("results");

    // Base scores by difficulty
    const baseScores = {
      1: 1000,
      2: 1500,
      3: 2000,
    };

    const targetTimes = {
      1: 420,
      2: 300,
      3: 240,
    };

    const targetTime = targetTimes[difficulty];
    let timeMultiplier = 1.0;

    if (timeElapsed < targetTime) {
      const speedRatio = timeElapsed / targetTime;
      timeMultiplier = Math.min(1.25, 1 + (1 - speedRatio) * 0.5);
    } else if (timeElapsed > targetTime * 2) {
      timeMultiplier = Math.max(
        0.75,
        1 - (timeElapsed - targetTime * 2) / (targetTime * 6)
      );
    } else {
      timeMultiplier = 1 - (timeElapsed - targetTime) / (targetTime * 4);
    }

    let finalScore = Math.round(baseScores[difficulty] * timeMultiplier);

    if (isComplete) {
      finalScore += Math.round(baseScores[difficulty] * 0.15);
    }

    const errorPenalty = errors * 25;
    const hintPenalty = hintsUsed * 75;

    finalScore = Math.max(0, finalScore - errorPenalty - hintPenalty);

    setScore(finalScore);
    setGameWasCompleted(isComplete); // 🔥 only allow saving score if puzzle was completed
  };

  // Check completion function remains the same
  const checkCompletion = (board) => {
    // Check if board has any empty cells
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (board[row][col] === 0) {
          return false;
        }
      }
    }

    // Check if board matches solution
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (board[row][col] !== solvedBoard[row][col]) {
          return false;
        }
      }
    }

    return true;
  };

  // Handle cell selection
  const handleCellSelect = (row, col) => {
    if (!isPlaying || isComplete) return;

    // Don't allow selecting cells that were pre-filled
    if (originalBoard[row][col] !== 0) return;

    setSelectedCell([row, col]);
  };

  // Handle number input
  const handleNumberInput = (num) => {
    if (!selectedCell || !isPlaying || isComplete) return;

    const [row, col] = selectedCell;

    // Don't allow modifying pre-filled cells
    if (originalBoard[row][col] !== 0) return;

    if (isNoteMode) {
      // Update notes
      const newNotes = [...notes];
      newNotes[row][col][num - 1] = !newNotes[row][col][num - 1];
      setNotes(newNotes);
    } else {
      // Place number on board
      const newBoard = [...sudokuBoard];

      // If number is already there, remove it
      if (newBoard[row][col] === num) {
        newBoard[row][col] = 0;
      } else {
        newBoard[row][col] = num;

        // Check if it's a valid move
        if (num !== solvedBoard[row][col]) {
          setErrors((prev) => prev + 1);
          setMessage("That's not correct");
          setShowMessage(true);
          setTimeout(() => setShowMessage(false), 2000);
        } else {
          // Clear notes for this cell
          const newNotes = [...notes];
          newNotes[row][col] = Array(9).fill(false);
          setNotes(newNotes);
        }
      }

      setSudokuBoard(newBoard);

      // Check if the board is complete
      if (checkCompletion(newBoard)) {
        setIsComplete(true);
        setMessage("Puzzle completed!");
        setShowMessage(true);
        setTimeout(() => {
          endGame();
        }, 2000);
      }
    }
  };

  // Use a hint
  const useHint = () => {
    if (!selectedCell || !isPlaying || isComplete) return;

    const [row, col] = selectedCell;

    // Don't provide hints for pre-filled cells
    if (originalBoard[row][col] !== 0) return;

    // Provide the correct number
    const newBoard = [...sudokuBoard];
    newBoard[row][col] = solvedBoard[row][col];
    setSudokuBoard(newBoard);

    // Clear notes for this cell
    const newNotes = [...notes];
    newNotes[row][col] = Array(9).fill(false);
    setNotes(newNotes);

    // Increment hint counter
    setHintsUsed((prev) => prev + 1);

    // Check if the board is complete
    if (checkCompletion(newBoard)) {
      setIsComplete(true);
      setMessage("Puzzle completed!");
      setShowMessage(true);
      setTimeout(() => {
        endGame();
      }, 2000);
    }
  };

  // Clear cell
  const clearCell = () => {
    if (!selectedCell || !isPlaying || isComplete) return;

    const [row, col] = selectedCell;

    // Don't allow clearing pre-filled cells
    if (originalBoard[row][col] !== 0) return;

    const newBoard = [...sudokuBoard];
    newBoard[row][col] = 0;
    setSudokuBoard(newBoard);
  };

  // Toggle notes mode
  const toggleNoteMode = () => {
    setIsNoteMode(!isNoteMode);
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // ----------------------------------
  //         SAVE SCORE
  // ----------------------------------
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // 🧠 Resolve userName with fallbacks
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // 🧱 Unified gameData object
      const gameData = {
        gameType: "sudoku-challenge",
        gameId: "sudoku-challenge",
        mode: "single",
        userId: user.email,
        userName,
        score,
        difficulty,
        timeElapsed,
        errors,
        hintsUsed,
        timestamp: new Date(),
      };

      // 🔗 Firebase references
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "sudoku-challenge"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // 🧠 Sorting: score > timeElapsed > fewer errors > fewer hints
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        if (a.timeElapsed !== b.timeElapsed)
          return a.timeElapsed - b.timeElapsed;
        if (a.errors !== b.errors) return a.errors - b.errors;
        if (a.hintsUsed !== b.hintsUsed) return a.hintsUsed - b.hintsUsed;
        return a.timestamp - b.timestamp;
      };

      // 🔁 Transaction to update leaderboard + user score
      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const difficultyKey = difficulty.toString();
        let currentLeaderboard =
          leaderboardData.leaderboards[difficultyKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(
            gameData,
            currentLeaderboard[currentLeaderboard.length - 1]
          ) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[difficultyKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // 💾 Save to gameHistory
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );

      await setDoc(gameHistoryRef, gameData);

      // 🔄 Reset game state
      setGameMode("intro");
      setGameStarted(false);
    } catch (err) {
      console.error("Error saving Sudoku score:", err);
    }
  };

  // ----------------------------------
  //         TUTORIAL LOGIC
  // ----------------------------------
  const advanceTutorial = () => {
    if (tutorialStep < 3) {
      setTutorialStep((prev) => prev + 1);
    } else {
      setGameMode("play");
      startGame();
    }
  };

  // ----------------------------------
  //            RENDER
  // ----------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-yellow-300">
              Sudoku Challenge
            </h1>
            <div className="p-2 bg-yellow-500/10 rounded-full">
              <Brain className="text-yellow-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions Modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-500/10 rounded-lg">
                      <Brain className="text-yellow-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      How Sudoku Challenge Works
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6 text-slate-300">
                  <p>
                    Sudoku Challenge helps build logical reasoning, working
                    memory, and pattern recognition - cognitive skills that are
                    valuable during recovery.
                  </p>
                  <p>
                    Fill in the 9×9 grid so that each column, row, and 3×3 box
                    contains the digits 1-9 without repetition.
                  </p>
                  <ul className="list-disc ml-5 space-y-2">
                    <li>
                      Your score is based on difficulty level, completion time,
                      and how many errors and hints you use.
                    </li>
                    <li>Use notes to track possible numbers for each cell.</li>
                    <li>
                      Choose from three difficulty levels to match your skill
                      level.
                    </li>
                  </ul>
                  <p>
                    Sudoku engages multiple brain regions, promoting focused
                    attention and enhancing your problem-solving abilities.
                  </p>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white py-3 rounded-lg font-medium hover:from-yellow-500 hover:to-orange-600 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Outer Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Brain size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Sudoku Challenge
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  Enhance logical reasoning, working memory, and pattern
                  recognition with this classic number puzzle.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Brain size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Logical Reasoning
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Strengthen deductive thinking and strategic planning skills.
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Focus & Attention
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Improve concentration and sustained attention through
                    puzzle-solving.
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-yellow-500/10 rounded-full mb-2">
                    <Trophy size={24} className="text-yellow-300" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Multiple Difficulties
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Choose from easy, medium, or hard puzzles to match your
                    skill level.
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {[1, 2, 3].map((lvl) => (
                      <button
                        key={lvl}
                        onClick={() => setDifficulty(lvl)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                          difficulty === lvl
                            ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-white"
                            : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                        }`}
                      >
                        {lvl === 1 ? "Easy" : lvl === 2 ? "Medium" : "Hard"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => setGameMode("play")}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* High Scores */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Time
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((s, index) => {
                          let formattedName = s.userName;
                          if (s.userName && s.userName.includes(" ")) {
                            const parts = s.userName.split(" ");
                            const firstName = parts[0];
                            const lastInitial = parts[1]
                              ? parts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }
                          return (
                            <tr
                              key={`${s.userId}-${index}`}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {formatTime(s.timeElapsed)}
                              </td>
                              <td className="py-2 px-4 text-center text-yellow-300 font-medium">
                                {s.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Sudoku Challenge
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4 space-y-4 text-slate-300">
                  {tutorialStep === 0 && (
                    <>
                      <p>
                        Sudoku is a logic-based number placement puzzle. The
                        goal is to fill a 9×9 grid so that each column, each
                        row, and each of the nine 3×3 boxes contains all of the
                        digits from 1 to 9 without repetition.
                      </p>
                      <p>
                        The puzzle starts with some cells already filled with
                        numbers. These are fixed and can't be changed.
                      </p>
                    </>
                  )}
                  {tutorialStep === 1 && (
                    <>
                      <p>
                        To play, select an empty cell by clicking on it. Then,
                        select a number from 1-9 to place in that cell.
                      </p>
                      <p>
                        You can also use "note mode" to mark possible numbers
                        for a cell without committing to a choice. Toggle note
                        mode by clicking the pencil icon.
                      </p>
                    </>
                  )}
                  {tutorialStep === 2 && (
                    <>
                      <p>
                        If you're stuck, you can use a hint to reveal the
                        correct number for the selected cell. However, using
                        hints will reduce your final score.
                      </p>
                      <p>
                        Your score is based on the difficulty level, completion
                        time, and the number of errors and hints used. Faster
                        times with fewer mistakes earn higher scores!
                      </p>
                    </>
                  )}
                  {tutorialStep === 3 && (
                    <>
                      <p>
                        // Continuing from previous artifact... Sudoku helps
                        strengthen logical reasoning, pattern recognition, and
                        working memory - all important cognitive skills that can
                        be affected during recovery.
                      </p>
                      <p>Ready to try a puzzle? Let's begin!</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                {tutorialStep > 0 ? (
                  <button
                    onClick={() => setTutorialStep((prev) => prev - 1)}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                ) : (
                  <button
                    onClick={() => setGameMode("intro")}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={advanceTutorial}
                  className="py-2 px-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors"
                >
                  {tutorialStep < 3 ? "Next" : "Start Game"}
                </button>
              </div>
            </div>
          )}

          {/* Play Screen */}
          {gameMode === "play" && (
            <div className="relative p-6 md:p-8">
              {!gameStarted && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                  <button
                    onClick={() => {
                      setGameStarted(true);
                      startGame();
                    }}
                    className="py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg"
                  >
                    Start Game
                  </button>
                </div>
              )}

              {/* Message toast */}
              <AnimatePresence>
                {showMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-slate-800 px-4 py-2 rounded-lg shadow-lg z-40"
                  >
                    {message}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex justify-between items-center mb-6">
                <div className="bg-gradient-to-r from-yellow-400/20 to-orange-500/20 px-4 py-2 rounded-lg">
                  <span className="text-yellow-200 font-medium">
                    Difficulty:{" "}
                    {difficulty === 1
                      ? "Easy"
                      : difficulty === 2
                      ? "Medium"
                      : "Hard"}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeElapsed)}
                    </span>
                  </div>

                  <button
                    onClick={() => {
                      setIsPaused(!isPaused);
                    }}
                    className="ml-2 p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 hover:text-white transition-colors"
                  >
                    {isPaused ? "Resume" : "Pause"}
                  </button>
                </div>
              </div>

              {/* Pause overlay */}
              {isPaused && (
                <div className="absolute inset-0 z-40 flex items-center justify-center bg-slate-900/80">
                  <div className="text-center p-6 bg-slate-800 rounded-xl">
                    <h3 className="text-xl font-bold text-white mb-4">
                      Game Paused
                    </h3>
                    <button
                      onClick={() => setIsPaused(false)}
                      className="py-2 px-4 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg"
                    >
                      Resume Game
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-col md:flex-row gap-8 items-start justify-center">
                {/* Sudoku board */}
                <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-1 md:p-2 mb-4 md:mb-0">
                  <div className="grid grid-cols-9 gap-0.5 md:gap-1">
                    {Array(9)
                      .fill()
                      .map((_, rowIdx) =>
                        Array(9)
                          .fill()
                          .map((_, colIdx) => {
                            const value = sudokuBoard[rowIdx]?.[colIdx] || 0;
                            const isOriginal =
                              originalBoard[rowIdx]?.[colIdx] !== 0;
                            const isSelected =
                              selectedCell &&
                              selectedCell[0] === rowIdx &&
                              selectedCell[1] === colIdx;
                            const sameBox =
                              selectedCell &&
                              Math.floor(rowIdx / 3) ===
                                Math.floor(selectedCell[0] / 3) &&
                              Math.floor(colIdx / 3) ===
                                Math.floor(selectedCell[1] / 3);
                            const sameRow =
                              selectedCell && rowIdx === selectedCell[0];
                            const sameCol =
                              selectedCell && colIdx === selectedCell[1];
                            const sameValue =
                              selectedCell &&
                              value !== 0 &&
                              value ===
                                sudokuBoard[selectedCell[0]]?.[selectedCell[1]];

                            // Determine cell background
                            let cellBg = "bg-slate-700/50";
                            if (isSelected) {
                              cellBg = "bg-yellow-500/30";
                            } else if (sameValue && value !== 0) {
                              cellBg = "bg-yellow-500/20";
                            } else if (sameRow || sameCol) {
                              cellBg = "bg-slate-700/80";
                            } else if (sameBox) {
                              cellBg = "bg-slate-700/70";
                            }

                            // Border styling for 3x3 boxes
                            let borderStyles = "";
                            if (colIdx % 3 === 0 && colIdx !== 0) {
                              borderStyles += " border-l-2 border-l-slate-600";
                            }
                            if (rowIdx % 3 === 0 && rowIdx !== 0) {
                              borderStyles += " border-t-2 border-t-slate-600";
                            }

                            return (
                              <button
                                key={`${rowIdx}-${colIdx}`}
                                onClick={() => handleCellSelect(rowIdx, colIdx)}
                                disabled={isPaused}
                                className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center ${cellBg} ${borderStyles} transition-colors relative`}
                              >
                                {value !== 0 ? (
                                  <span
                                    className={`text-sm md:text-lg font-medium ${
                                      isOriginal
                                        ? "text-white"
                                        : "text-yellow-300"
                                    }`}
                                  >
                                    {value}
                                  </span>
                                ) : (
                                  // Display notes
                                  <div className="grid grid-cols-3 grid-rows-3 gap-0 w-full h-full">
                                    {Array(9)
                                      .fill()
                                      .map((_, noteIdx) => (
                                        <div
                                          key={noteIdx}
                                          className="flex items-center justify-center"
                                        >
                                          {notes[rowIdx]?.[colIdx]?.[
                                            noteIdx
                                          ] && (
                                            <span className="text-[8px] md:text-[10px] text-slate-400">
                                              {noteIdx + 1}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </button>
                            );
                          })
                      )}
                  </div>
                </div>

                {/* Controls */}
                <div className="md:w-52 space-y-6">
                  {/* Game info */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3">Game Info</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm">Errors</span>
                        <span className="text-red-300 font-medium">
                          {errors}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm">
                          Hints Used
                        </span>
                        <span className="text-yellow-300 font-medium">
                          {hintsUsed}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Number controls */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-white font-medium">Numbers</h3>
                      <button
                        onClick={toggleNoteMode}
                        className={`p-2 rounded-lg ${
                          isNoteMode
                            ? "bg-yellow-500/30 text-yellow-300"
                            : "bg-slate-700 text-slate-300"
                        } transition-colors`}
                        title="Toggle note mode"
                      >
                        📝
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                        <button
                          key={num}
                          onClick={() => handleNumberInput(num)}
                          disabled={isPaused}
                          className="py-3 font-medium rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="space-y-2">
                    <button
                      onClick={clearCell}
                      disabled={!selectedCell || isPaused}
                      className="w-full py-2 px-4 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:pointer-events-none text-slate-300 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <XCircle size={16} />
                      Clear Cell
                    </button>

                    <button
                      onClick={useHint}
                      disabled={!selectedCell || isPaused}
                      className="w-full py-2 px-4 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:pointer-events-none text-slate-300 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <HelpCircle size={16} />
                      Hint (-100 pts)
                    </button>

                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            "Are you sure you want to end the game early?"
                          )
                        ) {
                          endGame();
                        }
                      }}
                      className="w-full py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors text-sm mt-4"
                    >
                      End Game
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Puzzle {isComplete ? "Completed!" : "Results"}
                </h2>
                <p className="text-slate-300">
                  Your Sudoku Challenge session has ended.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-yellow-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-yellow-300">
                        {score}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium">
                        {difficulty === 1
                          ? "Easy"
                          : difficulty === 2
                          ? "Medium"
                          : "Hard"}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Completion</span>
                      <span
                        className={`font-medium ${
                          isComplete ? "text-green-300" : "text-red-300"
                        }`}
                      >
                        {isComplete ? "Complete" : "Incomplete"}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Time Spent</span>
                      <span className="text-white">
                        {formatTime(timeElapsed)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Errors Made</span>
                      <span className="text-red-300">{errors}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Hints Used</span>
                      <span className="text-yellow-300">{hintsUsed}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Brain size={20} className="text-yellow-300" />
                    Cognitive Benefits
                  </h3>
                  <div className="space-y-3 text-slate-300">
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Logical Reasoning:
                      </span>{" "}
                      Sudoku challenges your ability to use logical deduction to
                      eliminate possibilities and find solutions.
                    </p>
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Working Memory:
                      </span>{" "}
                      Keeping track of possible numbers and patterns engages
                      your short-term memory capacity.
                    </p>
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Focus and Attention:
                      </span>{" "}
                      Maintaining concentration throughout the puzzle builds
                      sustained attention skills.
                    </p>
                    <p>
                      <span className="text-yellow-300 font-medium">
                        Cognitive Flexibility:
                      </span>{" "}
                      Switching between different strategies and rules supports
                      cognitive flexibility.
                    </p>
                    <p className="mt-4 text-sm">
                      Regular practice with puzzles like Sudoku can help
                      strengthen these cognitive domains that may be affected
                      during recovery.
                    </p>
                  </div>
                </div>
              </div>

              {/* Scoring explanation */}
              <div className="mb-6 bg-slate-800/50 rounded-lg p-4 text-sm text-slate-300">
                <h3 className="text-white font-medium mb-2">
                  How Scoring Works
                </h3>
                <p>
                  Base score: {1000 * difficulty} (Difficulty multiplier)
                  <br />
                  Time bonus: {Math.max(0, 3600 - timeElapsed)} (Faster
                  completion = higher bonus)
                  <br />
                  Error penalty: -{errors * 50} ({errors} errors)
                  <br />
                  Hint penalty: -{hintsUsed * 100} ({hintsUsed} hints)
                  <br />
                  {!isComplete && (
                    <span className="text-red-300">
                      Incomplete puzzle: reduced score
                    </span>
                  )}
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameWasCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}

                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SudokuChallenge;
